import { ArrowUpDown, ListFilter, X } from 'lucide-react';
import { Fragment, useEffect, useRef, useState } from 'react';

import { SearchInput } from '@/_shared/components/SearchInput';
import { Button } from '@/_shared/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/_shared/components/ui/dropdown-menu';
import { useDebouncedValue } from '@/_shared/hooks/useDebouncedValue';
import {
  encodeMultiFilter,
  parseMultiFilter,
  type SortDir,
  type SortState,
  type ViewMode,
} from '@/_shared/lib/list-controls';
import { cn } from '@/_shared/lib/utils';

import { ViewToggle } from './ViewToggle';

export interface SortOption {
  /** Matches a `sortKeys` entry from `useListControls`. */
  value: string;
  label: string;
}

export interface FilterOption {
  /** Any non-empty string. Empty means "no filter" everywhere in the list stack (`isActive`, `filterChips`). */
  value: string;
  label: string;
}

/** One filter dimension (e.g. "Status"). Single-select by default; multiple groups = one selection per group. */
export interface FilterGroup {
  key: string;
  label: string;
  options: readonly FilterOption[];
  /**
   * Opt-in MULTI-select: the group renders as checkboxes, with these values checked when the filter is
   * absent, and no "All" row (the default is not necessarily all). The value is encoded with
   * `encodeMultiFilter` / read with `parseMultiFilter`. Omit for the single-select radio group.
   */
  defaultValues?: readonly string[];
}

interface ListToolbarProps {
  query: string;
  onQueryChange: (query: string) => void;
  searchPlaceholder?: string;
  /**
   * The card/list view toggle. Omit BOTH for a list that renders one way only (Settings › Warehouses
   * is a table and nothing else); a toggle that switches between one view and itself is a dead
   * affordance.
   */
  view?: ViewMode;
  onViewChange?: (view: ViewMode) => void;
  sortOptions: readonly SortOption[];
  sort: SortState<string>;
  onSortChange: (sort: SortState<string>) => void;
  /** The default sort — the sort chip appears only when the current sort differs from it. */
  defaultSort: SortState<string>;
  /** Grouped, single-select-per-group filters. Omit for a list without filters. */
  filterGroups?: readonly FilterGroup[];
  filters?: Partial<Record<string, string | null>>;
  onFilterChange?: (key: string, value: string | null) => void;
  /** Clear every filter in one op (used by the menu's "Clear filters"). Omit to fall back to per-group. */
  onClearFilters?: () => void;
  /**
   * Reset the search, the filters, and the sort in one op — wired to the "Clear all" affordance, which
   * renders whenever there are chips. Required, not optional: the toolbar can't reconstruct this from its
   * other props (the query and the persisted sort live in the caller's state), and an unwired handler
   * would leave a visible button that does nothing.
   */
  onClearAll: () => void;
  /** Total items before search/filters — drives the item count ("N routines" / "X of Y routines"). */
  totalCount?: number;
  /** Items remaining after search/filters. Below `totalCount` ⇒ the count reads "X of Y…". */
  resultCount?: number;
  /**
   * Singular/plural noun for the item count (e.g. `{ singular: 'routine', plural: 'routines' }`). Supplying
   * it shows a persistent "N <noun>" at rest; without it the count appears only while narrowed ("X of Y").
   */
  itemNoun?: { singular: string; plural: string };
  /** Extra page controls, rendered before the Filter and Sort buttons (e.g. a refresh button). Opt-in. */
  actions?: React.ReactNode;
  className?: string;
}

/**
 * The shared list-page control bar: search, a grouped filter menu, a sort menu, and the card/list view
 * toggle, with a chips row below that surfaces every active filter + non-default sort (each removable)
 * and a "Clear all". Purely presentational — state comes from `useListControls`.
 */
/** Quiet window before a keystroke commits to the page filter — long enough to coalesce typing, short
 *  enough to feel live. Matches the per-page debounce this toolbar replaced. */
const SEARCH_DEBOUNCE_MS = 150;

export function ListToolbar({
  query,
  onQueryChange,
  searchPlaceholder = 'Search…',
  view,
  onViewChange,
  sortOptions,
  sort,
  onSortChange,
  defaultSort,
  filterGroups,
  filters,
  onFilterChange,
  onClearFilters,
  onClearAll,
  totalCount,
  resultCount,
  itemNoun,
  actions,
  className,
}: ListToolbarProps): React.ReactElement {
  // Search-as-you-type lives HERE, not in the page: a keystroke re-renders only this toolbar, while the
  // page's committed `query` (and the virtualized list it feeds) updates only once the debounce settles.
  // An emptied box still un-narrows at once — `useDebouncedValue` flushes an empty value synchronously.
  const [text, setText] = useState(query);
  const debouncedText = useDebouncedValue(text, SEARCH_DEBOUNCE_MS);
  // One guard for BOTH directions so `query` and `text` can't ping-pong: it holds the last value that
  // crossed the boundary, whether we pushed it up (the debounce below) or the page pushed it down (Clear /
  // Clear all set `query` to '').
  const lastEmitted = useRef(query);
  useEffect(() => {
    if (query === lastEmitted.current) return; // our own debounced value echoing back — already in sync
    lastEmitted.current = query;
    setText(query);
  }, [query]);
  useEffect(() => {
    if (debouncedText === lastEmitted.current) return;
    lastEmitted.current = debouncedText;
    onQueryChange(debouncedText);
  }, [debouncedText, onQueryChange]);

  const filterChips = (filterGroups ?? []).flatMap((group) => {
    const value = filters?.[group.key];
    // Only null/undefined/empty-string count as inactive — a legitimate value like '0' must survive.
    if (value == null || value === '') return [];
    const optionLabel = (v: string): string => group.options.find((o) => o.value === v)?.label ?? v;
    const label =
      group.defaultValues === undefined
        ? optionLabel(value)
        : parseMultiFilter(value, group.defaultValues).map(optionLabel).join(', ') || 'None';
    return [
      { id: `f:${group.key}`, label: `${group.label}: ${label}`, remove: () => onFilterChange?.(group.key, null) },
    ];
  });
  const isDefaultSort = sort.key === defaultSort.key && sort.dir === defaultSort.dir;
  const sortLabel = sortOptions.find((o) => o.value === sort.key)?.label ?? sort.key;
  const sortChip = isDefaultSort
    ? null
    : {
        id: 'sort',
        label: `Sort: ${sortLabel} ${sort.dir === 'asc' ? '↑' : '↓'}`,
        remove: () => onSortChange(defaultSort),
      };
  const chips = [...filterChips, ...(sortChip ? [sortChip] : [])];

  // Item count — "N routines" at rest, "X of Y routines" while search/filters narrow the set (sort never
  // changes the count). The at-rest total needs a noun to read well, so it shows only when `itemNoun` is
  // given; without one, the count appears only while narrowed.
  const isNarrowed = typeof resultCount === 'number' && typeof totalCount === 'number' && resultCount < totalCount;
  let countLabel: string | null = null;
  if (typeof totalCount === 'number') {
    const noun = itemNoun ? (totalCount === 1 ? itemNoun.singular : itemNoun.plural) : '';
    const suffix = noun ? ` ${noun}` : '';
    if (isNarrowed) countLabel = `${resultCount} of ${totalCount}${suffix}`;
    else if (itemNoun) countLabel = `${totalCount}${suffix}`;
  }

  // Keep keyboard focus from falling to <body> when a chip / "Clear all" removes itself: after the row
  // re-renders, land focus on the next remaining chip, else the filter/sort/view controls.
  const chipsRowRef = useRef<HTMLDivElement>(null);
  const controlsRef = useRef<HTMLDivElement>(null);
  const refocusAfterRemoval = (): void => {
    requestAnimationFrame(() => {
      const nextChip = chipsRowRef.current?.querySelector<HTMLElement>('button');
      (nextChip ?? controlsRef.current?.querySelector<HTMLElement>('button'))?.focus();
    });
  };
  // The item count is a persistent aria-live region whenever the list tracks counts (`totalCount` given),
  // so a screen reader announces "X of Y" as the user narrows — a region only added alongside its first
  // text is not reliably announced.
  const showCountRegion = typeof totalCount === 'number';

  return (
    <div className={cn('flex flex-col gap-2', className)}>
      <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
        <SearchInput
          value={text}
          onChange={setText}
          placeholder={searchPlaceholder}
          className="sm:max-w-xs sm:flex-1"
        />
        <div ref={controlsRef} className="flex items-center gap-2 sm:ml-auto">
          {actions}
          {filterGroups && filterGroups.length > 0 && (
            <FilterMenu
              groups={filterGroups}
              filters={filters ?? {}}
              activeCount={filterChips.length}
              onChange={(key, value) => onFilterChange?.(key, value)}
              onClearFilters={onClearFilters ?? (() => filterGroups.forEach((g) => onFilterChange?.(g.key, null)))}
            />
          )}
          <SortMenu sortOptions={sortOptions} sort={sort} onSortChange={onSortChange} />
          {view !== undefined && onViewChange !== undefined && <ViewToggle value={view} onChange={onViewChange} />}
        </div>
      </div>

      {(chips.length > 0 || showCountRegion) && (
        <div className="flex items-center justify-between gap-3">
          {/* Active filters / non-default sort on the left; the item count on the right. */}
          <div ref={chipsRowRef} className="flex flex-wrap items-center gap-x-2 gap-y-1.5">
            {chips.map((chip) => (
              <button
                key={chip.id}
                type="button"
                onClick={() => {
                  chip.remove();
                  refocusAfterRemoval();
                }}
                aria-label={`Remove ${chip.label}`}
                className="inline-flex items-center gap-1 rounded-full border border-border bg-background px-2 py-0.5 text-xs text-muted-foreground focus-ring transition-colors hover:border-grey-200 hover:text-foreground"
              >
                {chip.label}
                <X aria-hidden className="size-3" />
              </button>
            ))}
            {chips.length > 0 && (
              <Button
                variant="tertiary"
                size="sm"
                onClick={() => {
                  onClearAll();
                  refocusAfterRemoval();
                }}
              >
                Clear all
              </Button>
            )}
          </div>
          {showCountRegion && (
            <span className="shrink-0 text-xs text-muted-foreground tabular-nums" aria-live="polite">
              {countLabel ?? ''}
            </span>
          )}
        </div>
      )}
    </div>
  );
}

/** Section-header treatment for the menu group titles — smaller + grey, so they read below the options. */
const MENU_SECTION_CLASS = 'text-xs text-muted-foreground';

/**
 * The "All" row's radio value. Radix needs a string, and `null` isn't one — so the unselected state maps
 * to the empty string, which the whole list stack already reads as "no filter" (`isActive`, `filterChips`).
 * A reserved word here (`'all'`) would instead make a legitimate option of that value silently unselectable.
 */
const NO_FILTER = '';

function FilterMenu({
  groups,
  filters,
  activeCount,
  onChange,
  onClearFilters,
}: {
  groups: readonly FilterGroup[];
  filters: Partial<Record<string, string | null>>;
  activeCount: number;
  onChange: (key: string, value: string | null) => void;
  onClearFilters: () => void;
}): React.ReactElement {
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="secondary"
          size="md"
          iconOnly={activeCount === 0}
          aria-label={activeCount > 0 ? `Filter (${activeCount} active)` : 'Filter'}
          Icon={<ListFilter />}
        >
          {activeCount > 0 ? activeCount : undefined}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-52">
        {groups.map((group, index) => (
          <Fragment key={group.key}>
            {index > 0 && <DropdownMenuSeparator />}
            <DropdownMenuLabel className={MENU_SECTION_CLASS}>{group.label}</DropdownMenuLabel>
            {group.defaultValues !== undefined ? (
              <MultiSelectOptions
                group={group}
                defaults={group.defaultValues}
                value={filters[group.key]}
                onChange={onChange}
              />
            ) : (
              <DropdownMenuRadioGroup
                value={filters[group.key] ?? NO_FILTER}
                onValueChange={(next) => onChange(group.key, next === NO_FILTER ? null : next)}
              >
                {/* preventDefault keeps the menu open so you can pick across multiple groups in one go. */}
                <DropdownMenuRadioItem value={NO_FILTER} onSelect={(e) => e.preventDefault()}>
                  All
                </DropdownMenuRadioItem>
                {group.options.map((option) => (
                  <DropdownMenuRadioItem key={option.value} value={option.value} onSelect={(e) => e.preventDefault()}>
                    {option.label}
                  </DropdownMenuRadioItem>
                ))}
              </DropdownMenuRadioGroup>
            )}
          </Fragment>
        ))}
        {activeCount > 0 && (
          <>
            <DropdownMenuSeparator />
            <DropdownMenuItem onSelect={onClearFilters}>Clear filters</DropdownMenuItem>
          </>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

/** A multi-select group's checkboxes. Kept in the option order whatever order they were ticked in. */
function MultiSelectOptions({
  group,
  defaults,
  value,
  onChange,
}: {
  group: FilterGroup;
  defaults: readonly string[];
  value: string | null | undefined;
  onChange: (key: string, value: string | null) => void;
}): React.ReactElement {
  const selected = parseMultiFilter(value, defaults);
  return (
    <>
      {group.options.map((option) => (
        <DropdownMenuCheckboxItem
          key={option.value}
          checked={selected.includes(option.value)}
          // preventDefault keeps the menu open so several boxes can be ticked in one go.
          onSelect={(e) => e.preventDefault()}
          onCheckedChange={(checked) => {
            const next = group.options
              .map((o) => o.value)
              .filter((v) => (v === option.value ? checked === true : selected.includes(v)));
            onChange(group.key, encodeMultiFilter(next, defaults));
          }}
        >
          {option.label}
        </DropdownMenuCheckboxItem>
      ))}
    </>
  );
}

function SortMenu({
  sortOptions,
  sort,
  onSortChange,
}: {
  sortOptions: readonly SortOption[];
  sort: SortState<string>;
  onSortChange: (sort: SortState<string>) => void;
}): React.ReactElement {
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="secondary" size="md" iconOnly aria-label="Sort" Icon={<ArrowUpDown />} />
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-48">
        <DropdownMenuLabel className={MENU_SECTION_CLASS}>Sort by</DropdownMenuLabel>
        <DropdownMenuRadioGroup value={sort.key} onValueChange={(key) => onSortChange({ key, dir: sort.dir })}>
          {sortOptions.map((option) => (
            <DropdownMenuRadioItem key={option.value} value={option.value} onSelect={(e) => e.preventDefault()}>
              {option.label}
            </DropdownMenuRadioItem>
          ))}
        </DropdownMenuRadioGroup>
        <DropdownMenuSeparator />
        <DropdownMenuLabel className={MENU_SECTION_CLASS}>Direction</DropdownMenuLabel>
        <DropdownMenuRadioGroup
          value={sort.dir}
          onValueChange={(dir) => onSortChange({ key: sort.key, dir: dir as SortDir })}
        >
          <DropdownMenuRadioItem value="asc" onSelect={(e) => e.preventDefault()}>
            Ascending
          </DropdownMenuRadioItem>
          <DropdownMenuRadioItem value="desc" onSelect={(e) => e.preventDefault()}>
            Descending
          </DropdownMenuRadioItem>
        </DropdownMenuRadioGroup>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
